import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Search, MapPin, MessageCircle, Briefcase } from 'lucide-react';
import { CommunityLayout } from '../components/CommunityLayout';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { chatAPI, PROFESSIONAL_TYPES, PROVINCES, ProfessionalInDirectory } from '../services/chatService';

const veldKlasse =
  'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]';

// Smoelenboek (Peer Directory): vaste profielkaart met de vier pijlers
// voornaam, foto, beroep en woonplaats, plus de live online-status.
// Bewust geen ratings, likes of rechtsvormen.
const ProfielKaart: React.FC<{ professional: ProfessionalInDirectory; onChat: () => void; bezig: boolean }> = ({
  professional: p,
  onChat,
  bezig,
}) => {
  const { t } = useTranslation('common');
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col items-center text-center shadow-sm">
      <BelagomProfilePhoto src={p.profile_photo_path} alt={p.first_name} size={96} isOnline={p.is_online} />
      <h3 className="mt-3 font-semibold text-gray-900">{p.first_name}</h3>
      <p className="mt-1 text-sm text-gray-600 flex items-start justify-center gap-1">
        <Briefcase className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{p.professional_type_label}</span>
      </p>
      <p className="mt-1 text-sm text-gray-500 flex items-center justify-center gap-1">
        <MapPin className="w-4 h-4 shrink-0" />
        {p.city || t('smoelenboek.woonplaatsOnbekend')}
      </p>
      <span
        className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
          p.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
        }`}
      >
        <span className={`w-2 h-2 rounded-full ${p.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
        {p.is_online ? t('smoelenboek.online') : t('smoelenboek.offline')}
      </span>
      <button
        type="button"
        onClick={onChat}
        disabled={bezig}
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-medium text-white hover:bg-[#13305a] disabled:opacity-60"
      >
        <MessageCircle className="w-4 h-4" />
        {t('smoelenboek.chat')}
      </button>
    </div>
  );
};

export const CommunityProfessionalsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [zoek, setZoek] = useState('');
  const [beroep, setBeroep] = useState('');
  const [provincie, setProvincie] = useState('');
  const [alleenOnline, setAlleenOnline] = useState(false);

  const { data: professionals = [], isLoading, isError } = useQuery({
    queryKey: ['professionals-directory', zoek, provincie, beroep],
    queryFn: () =>
      chatAPI.searchProfessionals({
        name: zoek || undefined,
        province: provincie || undefined,
        professional_type: beroep || undefined,
      }),
    staleTime: 30_000,
  });

  const startChat = useMutation({
    mutationFn: (id: string) => chatAPI.getOrCreatePeerConversation(id),
    onSuccess: (conv) => navigate(`/professionals/chat?conversation=${conv.id}`),
    onError: () => toast.error(t('smoelenboek.chatFout')),
  });

  const zichtbaar = alleenOnline ? professionals.filter((p) => p.is_online) : professionals;

  return (
    <CommunityLayout>
      <div className="px-4 sm:px-6 py-6">
        <p className="text-sm text-gray-600 max-w-3xl">{t('smoelenboek.intro')}</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder={t('smoelenboek.zoekPlaceholder')}
              className={`${veldKlasse} w-full pl-9`}
            />
          </div>
          <select value={beroep} onChange={(e) => setBeroep(e.target.value)} className={veldKlasse}>
            <option value="">{t('smoelenboek.alleBeroepen')}</option>
            {PROFESSIONAL_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select value={provincie} onChange={(e) => setProvincie(e.target.value)} className={veldKlasse}>
            <option value="">{t('smoelenboek.alleProvincies')}</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={alleenOnline} onChange={(e) => setAlleenOnline(e.target.checked)} />
            {t('smoelenboek.alleenOnline')}
          </label>
        </div>

        {isLoading ? (
          <p className="mt-8 text-sm text-gray-500">{t('smoelenboek.laden')}</p>
        ) : isError ? (
          <p className="mt-8 text-sm text-red-600">{t('smoelenboek.fout')}</p>
        ) : zichtbaar.length === 0 ? (
          <p className="mt-8 text-sm text-gray-500">{t('smoelenboek.geenResultaten')}</p>
        ) : (
          <>
            <p className="mt-6 text-xs text-gray-500">{t('smoelenboek.aantal', { count: zichtbaar.length })}</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {zichtbaar.map((p) => (
                <ProfielKaart
                  key={p.id}
                  professional={p}
                  onChat={() => startChat.mutate(p.id)}
                  bezig={startChat.isPending && startChat.variables === p.id}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </CommunityLayout>
  );
};
